/**
 * National Driving Guild — premium homepage.
 *
 * Layout matches the founder's reference image (IMG_3601.jpeg):
 *   1. Header — NDG shield + brand strap + desktop nav + Log In / Join CTAs
 *   2. Hero — eyebrow / headline / sub / 6 benefit bullets / 2 CTAs / instructor photo + handwritten testimonial
 *   3. Trust stat strip — 4 stats inside one dark rounded card
 *   4. Two-column grid:
 *        Left:  "Sound familiar?" + 6 pain pills + reassurance
 *        Right: "The all-in-one platform that changes everything." + 6 feature cards (NEW badge on AI)
 *   5. Bottom row — 5-star testimonial / community card / final-CTA card
 *   6. Trust footer row — 4 icon labels
 *
 * Palette: #050505 background, #D4AF37 / #F3E5AB gold, white text.
 * Mobile-first: stacks gracefully, all desktop layouts are 2- or 3-col only.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import CoreJourneyMantra from "../components/CoreJourneyMantra";
import { useReveal } from "../hooks/useReveal";
import { useSectionEngagement } from "../hooks/useSectionEngagement";
import { trackCta, trackEvent } from "../utils/analytics";
import {
  ArrowRight, Play, Check, ChevronDown, ChevronRight, Star, Users, ShieldCheck,
  TrendingUp, Clock, Calendar, XCircle, User, Megaphone, Frown,
  CalendarDays, Brain, IdCard, Gift, GraduationCap, Lock, BadgeCheck,
  Rocket, Quote, MapPin, Zap, Compass, ArrowRightLeft, Sparkles,
  HeartHandshake, MessagesSquare, Award, Eye, PoundSterling, Heart,
  Trophy, Target,
} from "lucide-react";

// Premium NDG hero asset — branded instructor vehicle with learner + instructor visible.
const HERO_IMG = "https://customer-assets.emergentagent.com/job_first-pass-1/artifacts/dmx3fjkg_IMG_3604.png";
const TESTIMONIAL_AVATARS = [
  "https://images.unsplash.com/photo-1656769539038-15c9799524c5?crop=entropy&cs=tinysrgb&fm=jpg&q=70&w=120&ixlib=rb-4.1.0",
  "https://images.unsplash.com/photo-1658134424788-d67ebbd8ef5e?crop=entropy&cs=tinysrgb&fm=jpg&q=70&w=120&ixlib=rb-4.1.0",
  "https://images.unsplash.com/photo-1667020854803-0305af085242?crop=entropy&cs=tinysrgb&fm=jpg&q=70&w=120&ixlib=rb-4.1.0",
];

export default function Landing() {
  const { user } = useAuth();
  const primaryHref = user ? "/dashboard" : "/register";

  // Inbound referral capture — homepage hit with ?ref=<member_id> means
  // someone clicked through from a Verified share-card. Fire once per visit
  // and stash the referrer in sessionStorage so the eventual signup can
  // attribute it.  Lightweight, runs on mount only.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = (params.get("ref") || "").trim();
      if (!ref) return;
      // Persist only the first ref of a session so attribution is stable.
      const sessionKey = "ndg_referrer_user_id";
      const already = window.sessionStorage?.getItem(sessionKey);
      if (!already) {
        window.sessionStorage?.setItem(sessionKey, ref);
        trackEvent("share_card_referral", { referrer_user_id: ref });
      }
    } catch {
      // Storage / URL parsing must never block the page.
    }
  }, []);

  return (
    <div className="min-h-dvh no-overflow-x bg-[#050505] text-white font-outfit antialiased overflow-x-hidden">
      {/* Ambient grain — premium tactile feel */}
      <div className="fixed inset-0 grain-overlay opacity-[0.05] pointer-events-none z-0" aria-hidden />

      <Header user={user} primaryHref={primaryHref} />

      <main className="relative">
        <Hero primaryHref={primaryHref} />
        <Reveal><AudiencePicker /></Reveal>
        <Reveal><MentalSupport /></Reveal>
        <Reveal><StatsStrip /></Reveal>
        <Reveal><PainAndPlatform /></Reveal>
        <Reveal><EasySwitch primaryHref={primaryHref} /></Reveal>
        <Reveal><GuildMembership primaryHref={primaryHref} /></Reveal>
        <Reveal><BottomRow primaryHref={primaryHref} /></Reveal>
        <Reveal><TrustFooter /></Reveal>
      </main>
    </div>
  );
}

/** Lightweight scroll-reveal wrapper — fades + lifts once on enter. */
function Reveal({ children, delay = 0 }) {
  const [ref, on] = useReveal();
  return (
    <div
      ref={ref}
      className={`reveal ${on ? "reveal-in" : ""}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* AUDIENCE PICKER — Who are you?                                      */
/* ─────────────────────────────────────────────────────────────────── */

const AUDIENCES = [
  {
    id: "learner",
    label: "I'm a Learner",
    sub: "Find the right instructor first time",
    bullets: ["Best Match quiz (above ↑)", "Verified instructors", "Manual / auto / female"],
    to: "/for-learners",
    icon: GraduationCap,
    cta: "See learner page",
    active: true,
  },
  {
    id: "instructor",
    label: "I'm an Instructor",
    sub: "Tools, trust, and a real community",
    bullets: ["AI lesson review", "Standards Check prep", "Marketplace visibility"],
    to: "/register?role=adi",
    icon: ShieldCheck,
    cta: "Join the Guild",
  },
  {
    id: "pdi",
    label: "I'm a PDI",
    sub: "Part 1 → qualified ADI, guided",
    bullets: ["Part 1 / 2 / 3 prep", "Mock Part 3 sim", "Find a trainer"],
    to: "/for-pdis",
    icon: Award,
    cta: "See PDI track",
  },
];

function AudiencePicker() {
  return (
    <section id="audiences" className="relative py-12 md:py-16" data-testid="audience-picker">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        <div className="text-center max-w-2xl mx-auto mb-8 md:mb-10">
          <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#D4AF37] font-bold mb-3 inline-flex items-center gap-2 justify-center">
            <Users className="w-3 h-3" /> Who are you?
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-white leading-tight">
            One Guild.<br />
            <span className="text-gold-gradient">Three pathways.</span>
          </h2>
          <p className="mt-3 text-[14.5px] text-zinc-300 leading-relaxed">
            Pick the one that fits — we'll take you to the right page.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {AUDIENCES.map((a) => {
            const Icon = a.icon;
            const Wrapper = a.to ? Link : "div";
            const wrapperProps = a.to ? { to: a.to } : {};
            return (
              <Wrapper
                key={a.id}
                {...wrapperProps}
                data-testid={`audience-card-${a.id}`}
                className={`group relative rounded-2xl border p-6 transition block ${
                  a.active
                    ? "border-[#D4AF37]/45 bg-gradient-to-br from-[#16120a] via-[#0c0c0c] to-[#0a0a0a] guild-card-glow"
                    : "border-white/[0.07] bg-white/[0.025] hover:border-[#D4AF37]/40 hover:bg-white/[0.035] cursor-pointer"
                }`}
              >
                {a.active && (
                  <span className="absolute -top-2.5 left-5 inline-flex items-center gap-1 text-[9.5px] uppercase tracking-[0.22em] font-bold bg-[#D4AF37] text-[#050505] rounded-full px-2 py-0.5">
                    Hero promise
                  </span>
                )}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  a.active
                    ? "bg-[#D4AF37]/20 border border-[#D4AF37]/40"
                    : "bg-[#D4AF37]/10 border border-[#D4AF37]/25"
                }`}>
                  <Icon className="w-5.5 h-5.5 text-[#D4AF37]" />
                </div>
                <div className="font-display text-xl md:text-2xl font-semibold text-white">{a.label}</div>
                <div className="mt-1.5 text-[13.5px] text-zinc-300">{a.sub}</div>
                <ul className="mt-5 space-y-2">
                  {a.bullets.map((b, i) => (
                    <li key={i} className="flex items-center gap-2 text-[13px] text-zinc-300">
                      <Check className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className={`mt-6 inline-flex items-center gap-1.5 text-[12.5px] font-semibold ${
                  a.active ? "text-zinc-400" : "text-[#F3E5AB] group-hover:text-white transition"
                }`}>
                  {a.cta}
                  {!a.active && <ChevronRight className="w-3.5 h-3.5" />}
                </div>
              </Wrapper>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* HEADER                                                              */
/* ─────────────────────────────────────────────────────────────────── */

function NdgShield({ size = 44 }) {
  return (
    <svg
      width={size}
      height={size * 1.1}
      viewBox="0 0 40 44"
      aria-hidden
      className="flex-shrink-0"
    >
      <defs>
        <linearGradient id="ndg-gold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F3E5AB" />
          <stop offset="0.5" stopColor="#D4AF37" />
          <stop offset="1" stopColor="#A37B1F" />
        </linearGradient>
      </defs>
      <path
        d="M20 1 L37 6 V20 C37 30 30 38 20 42 C10 38 3 30 3 20 V6 Z"
        fill="#0a0a0a"
        stroke="url(#ndg-gold)"
        strokeWidth="1.4"
      />
      <text
        x="20" y="18" textAnchor="middle"
        fontFamily="Outfit, sans-serif" fontWeight="900" fontSize="9"
        fill="url(#ndg-gold)" letterSpacing="0.5"
      >NDG</text>
      <g fill="url(#ndg-gold)">
        <polygon points="11,28 12,30.5 14.5,30.5 12.5,32 13.3,34.5 11,33 8.7,34.5 9.5,32 7.5,30.5 10,30.5" />
        <polygon points="20,28 21,30.5 23.5,30.5 21.5,32 22.3,34.5 20,33 17.7,34.5 18.5,32 16.5,30.5 19,30.5" />
        <polygon points="29,28 30,30.5 32.5,30.5 30.5,32 31.3,34.5 29,33 26.7,34.5 27.5,32 25.5,30.5 28,30.5" />
      </g>
    </svg>
  );
}

function Header({ user, primaryHref }) {
  return (
    <header className="sticky top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#050505]/80 border-b border-white/[0.07]">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 h-[72px] flex items-center justify-between gap-4">
        {/* Brand */}
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
          <NdgShield size={42} />
          <div className="hidden sm:block leading-tight">
            <div className="font-condensed text-white text-lg md:text-xl tracking-wider">
              NATIONAL<br className="md:hidden" /> <span className="md:inline">DRIVING GUILD</span>
            </div>
            <div className="text-[8.5px] md:text-[9px] uppercase tracking-[0.18em] text-zinc-500 font-medium">
              Find your right-fit instructor. First time.
            </div>
          </div>
        </Link>

        {/* Desktop nav — Best Match is the lead promise; pathways for each audience below */}
        <nav className="hidden lg:flex items-center gap-6 text-sm text-zinc-300">
          <Link to="/marketplace/match" data-testid="nav-best-match" className="text-white border-b-2 border-[#D4AF37] pb-0.5">Best Match</Link>
          <Link to="/for-learners" data-testid="nav-learners" className="hover:text-white transition">For Learners</Link>
          <a href="#audiences" data-testid="nav-instructors" className="hover:text-white transition">For Instructors</a>
          <Link to="/for-pdis" data-testid="nav-pdis" className="hover:text-white transition">For PDIs</Link>
          <Link to="/pricing" data-testid="nav-pricing" className="hover:text-white transition">Pricing</Link>
        </nav>

        {/* Auth CTAs */}
        <div className="flex items-center gap-2">
          {user ? (
            <Link to="/dashboard">
              <Button
                data-testid="go-to-dashboard-btn"
                className="bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] hover:from-[#FAF0C0] hover:to-[#E5C04A] text-[#050505] font-bold rounded-md px-4 md:px-5 h-10 text-sm guild-cta-glow"
              >
                Dashboard
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/login">
                <Button
                  data-testid="login-btn"
                  variant="outline"
                  className="bg-transparent border-white/25 text-white hover:bg-white/5 font-semibold rounded-md h-10 px-4 md:px-5 text-sm"
                >
                  Log In
                </Button>
              </Link>
              <Link to={primaryHref}>
                <Button
                  data-testid="signup-btn"
                  className="bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] hover:from-[#FAF0C0] hover:to-[#E5C04A] text-[#050505] font-bold rounded-md h-10 px-4 md:px-5 text-sm guild-cta-glow"
                >
                  Join The Guild
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* HERO                                                                */
/* ─────────────────────────────────────────────────────────────────── */

const HERO_BULLETS = [
  { icon: Sparkles, title: "60-second quiz", body: "Answer 5 questions about how you learn — get your top 3 matches." },
  { icon: ShieldCheck, title: "Verified instructors only", body: "Every NDG instructor is DVSA-checked and reviewed." },
  { icon: Target, title: "Manual or automatic", body: "Both options on every profile — no awkward phone calls." },
  { icon: BadgeCheck, title: "Female instructors", body: "Filter and find one near you in seconds." },
  { icon: Brain, title: "Anxiety-aware coaching", body: "Patient, calm instructors clearly tagged." },
  { icon: TrendingUp, title: "Pass first time", body: "The right instructor is half the battle. We help you find them." },
];

function Hero({ primaryHref }) {
  // Learners (the 90% audience) see Best Match as the primary action.
  // Logged-in instructors land on /dashboard via the header CTA — they don't see this hero.
  const [sectionRef, markCtaClicked] = useSectionEngagement("hero");
  return (
    <section ref={sectionRef} className="relative pt-12 md:pt-14 pb-12 md:pb-16" data-testid="hero-section">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-10 items-center">
        {/* LEFT — copy */}
        <div className="lg:col-span-7 relative z-10">
          <div
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/35 bg-[#D4AF37]/[0.07] px-3 py-1 text-[10.5px] uppercase tracking-[0.24em] text-[#F3E5AB] font-semibold mb-6"
          >
            <Sparkles className="w-3 h-3" /> Best Match Quiz
          </div>

          <h1
            data-testid="hero-title"
            className="font-display font-semibold text-white tracking-tight leading-[1.02]"
            style={{ fontSize: "clamp(2.4rem, 5.4vw, 4.5rem)" }}
          >
            Find the right<br />
            driving instructor —<br />
            <span className="text-gold-gradient">first time.</span>
          </h1>

          <CoreJourneyMantra className="mt-5" testid="landing-hero-mantra" />

          <p
            data-testid="hero-subheadline"
            className="mt-6 md:mt-7 text-base md:text-lg text-zinc-300 leading-relaxed max-w-xl"
          >
            Take our 60-second Best Match quiz. We line up your top three verified instructors —
            patient, fast-track, manual, automatic, female, anxiety-aware. No more guessing.
          </p>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Link to="/marketplace/match" onClick={() => { markCtaClicked({ cta: "best_match" }); trackCta("landing_hero_cta_clicked", "Take the Best Match quiz", { destination: "/marketplace/match" }); }}>
              <Button
                data-testid="hero-cta-btn"
                className="h-13 px-6 rounded-md bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] hover:from-[#FAF0C0] hover:to-[#E5C04A] text-[#050505] text-[14px] font-bold guild-cta-glow w-full sm:w-auto"
              >
                Take the Best Match quiz <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="#audiences" onClick={() => { markCtaClicked({ cta: "instructor_jump" }); trackCta("landing_secondary_cta_clicked", "I'm an instructor", { destination: "#audiences" }); }}>
              <Button
                data-testid="hero-secondary-btn"
                variant="outline"
                className="h-13 px-6 rounded-md bg-transparent border border-[#D4AF37]/45 hover:bg-[#D4AF37]/[0.06] text-white text-[14px] font-semibold w-full sm:w-auto"
              >
                <ShieldCheck className="w-4 h-4 mr-2 text-[#D4AF37]" /> I'm an instructor
              </Button>
            </a>
          </div>

          <p
            data-testid="hero-trust-line"
            className="mt-5 text-[13px] text-zinc-400 inline-flex items-center gap-2"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span>
              <span className="text-white font-semibold">Free.</span> No signup needed to see your matches.
            </span>
          </p>
        </div>

        {/* RIGHT — branded instructor vehicle (cropped from NDG hero asset).
           Hidden on mobile (<lg) where the aggressive crop produced an
           awkward half-image; mobile keeps a clean text + CTA hero. */}
        <div className="hidden lg:block lg:col-span-5 relative">
          <div
            data-testid="hero-image-frame"
            className="relative aspect-[5/4] rounded-3xl overflow-hidden border border-[#D4AF37]/25 guild-card-glow bg-[#050505]"
            style={{
              backgroundImage: `url(${HERO_IMG})`,
              backgroundSize: "240%",
              backgroundPosition: "70% 38%",
              backgroundRepeat: "no-repeat",
            }}
            role="img"
            aria-label="National Driving Guild branded instructor vehicle with instructor and learner"
          >
            {/* SR-only image element so the alt text is preserved for assistive tech and SEO */}
            <img
              src={HERO_IMG}
              alt="National Driving Guild branded instructor vehicle with instructor and learner"
              className="sr-only"
              loading="eager"
            />
            {/* Hard left-edge mask — covers overlay text bleed-through from source screenshot */}
            <div
              className="absolute inset-y-0 left-0 pointer-events-none"
              style={{
                width: "38%",
                background: "linear-gradient(to right, #050505 0%, #050505 60%, rgba(5,5,5,0) 100%)",
              }}
              aria-hidden
            />
            <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#050505]/55 to-transparent pointer-events-none" aria-hidden />
            {/* Top eyebrow chip — instant context */}
            <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-black/60 backdrop-blur border border-[#D4AF37]/40 px-2.5 py-1 text-[9.5px] uppercase tracking-[0.22em] font-bold text-[#F3E5AB]">
              <BadgeCheck className="w-3 h-3" /> NDG Verified Vehicle
            </div>
          </div>
        </div>
      </div>

      {/* 6-bullet feature strip — premium descriptive cards beneath the hero */}
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 mt-10 md:mt-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {HERO_BULLETS.map(({ icon: Icon, title, body }, i) => (
            <article
              key={i}
              data-testid={`hero-bullet-${i}`}
              className="rounded-2xl bg-[#0a0a0a] border border-white/[0.07] p-4 md:p-5 hover:border-[#D4AF37]/35 transition"
            >
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#D4AF37]/20 to-transparent border border-[#D4AF37]/25 flex items-center justify-center mb-3">
                <Icon className="w-[18px] h-[18px] text-[#F3E5AB]" />
              </div>
              <h3 className="font-display text-[13.5px] md:text-sm font-semibold leading-tight mb-1.5">{title}</h3>
              <p className="text-[11.5px] text-zinc-400 leading-snug">{body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* TRUST STAT STRIP                                                    */
/* ─────────────────────────────────────────────────────────────────── */

/* ─────────────────────────────────────────────────────────────────── */
/* MENTAL SUPPORT — driving is mental as well as mechanical            */
/* ─────────────────────────────────────────────────────────────────── */

const MENTAL_BLOCKS = [
  {
    id: "learners",
    label: "Learners",
    icon: GraduationCap,
    bullets: [
      { icon: Heart,    text: "Nervous learner support" },
      { icon: Sparkles, text: "Calm progress" },
      { icon: Target,   text: "Confidence-building" },
    ],
  },
  {
    id: "pdis",
    label: "PDIs",
    icon: Award,
    bullets: [
      { icon: ShieldCheck, text: "Readiness confidence" },
      { icon: Eye,         text: "Honest feedback" },
      { icon: Heart,       text: "Less overwhelm" },
    ],
  },
  {
    id: "adis",
    label: "ADIs",
    icon: HeartHandshake,
    bullets: [
      { icon: Brain,       text: "Reduced admin stress" },
      { icon: ShieldCheck, text: "Standards confidence" },
      { icon: Sparkles,    text: "Better work-life flow" },
    ],
  },
];

function MentalSupport() {
  const [sectionRef] = useSectionEngagement("confidence");
  return (
    <section
      ref={sectionRef}
      id="mental-support"
      data-testid="mental-support"
      className="relative py-14 md:py-20"
    >
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
          <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#D4AF37] font-bold mb-3 inline-flex items-center gap-2 justify-center">
            <Heart className="w-3 h-3" /> The human side
          </div>
          <h2
            data-testid="mental-support-title"
            className="font-display text-3xl md:text-5xl font-semibold text-white leading-tight"
          >
            Driving is<br />
            <span className="text-gold-gradient">confidence too.</span>
          </h2>
          <p
            data-testid="mental-support-subheadline"
            className="mt-5 text-[15px] md:text-base text-zinc-300 leading-relaxed"
          >
            National Driving Guild helps learners, PDIs and instructors reduce stress,
            build confidence, and progress with clarity.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {MENTAL_BLOCKS.map(({ id, label, icon: Icon, bullets }) => (
            <div
              key={id}
              data-testid={`mental-block-${id}`}
              className="rounded-2xl border border-white/[0.07] bg-gradient-to-br from-[#0d0d0d] to-[#080808] p-6 hover:border-[#D4AF37]/35 transition"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div className="font-display text-xl font-semibold text-white">{label}</div>
              </div>
              <ul className="space-y-2.5">
                {bullets.map(({ icon: BIcon, text }, i) => (
                  <li
                    key={i}
                    data-testid={`mental-bullet-${id}-${i}`}
                    className="flex items-start gap-2.5 text-[13.5px] text-zinc-300 leading-snug"
                  >
                    <BIcon className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <p className="mt-8 md:mt-10 text-center text-[12.5px] text-zinc-500 max-w-2xl mx-auto leading-relaxed">
          We support the human being — not just the task.
        </p>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* STATS STRIP                                                         */
/* ─────────────────────────────────────────────────────────────────── */

const STATS = [
  { icon: Users, primary: "1,000+", label: "Instructors\nalready joined" },
  { icon: Star, primary: "4.9/5", label: "Average member\nsatisfaction", iconFill: true },
  { icon: ShieldCheck, primary: null, label: "Built by\ninstructors who\nunderstand you" },
  { icon: TrendingUp, primary: null, label: "More income.\nLess stress.\nBetter life." },
];

function StatsStrip() {
  return (
    <section className="relative pt-2 pb-14 md:pb-20">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        <div
          data-testid="stats-strip"
          className="rounded-2xl border border-white/[0.07] bg-[#0a0a0a]/80 backdrop-blur p-6 md:p-7 grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 md:gap-x-8 guild-card-glow"
        >
          {STATS.map(({ icon: Icon, primary, label, iconFill }, i) => (
            <div
              key={i}
              data-testid={`stat-${i}`}
              className="flex items-center gap-3 md:gap-4"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-[#D4AF37]/[0.10] border border-[#D4AF37]/25 flex items-center justify-center flex-shrink-0">
                <Icon className={`w-5 h-5 md:w-6 md:h-6 text-[#F3E5AB] ${iconFill ? "fill-[#F3E5AB]" : ""}`} />
              </div>
              <div className="min-w-0">
                {primary && (
                  <div className="font-display text-2xl md:text-3xl font-bold text-white leading-none mb-1">
                    {primary}
                  </div>
                )}
                <div className="text-[12px] md:text-[13px] text-zinc-300 leading-snug whitespace-pre-line">
                  {label}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* PAIN POINTS (left) + ALL-IN-ONE PLATFORM (right) — side by side     */
/* ─────────────────────────────────────────────────────────────────── */

const PAINS = [
  { icon: Clock, label: "Too much admin, not enough time" },
  { icon: Calendar, label: "Diary not full enough" },
  { icon: XCircle, label: "Cancellations and no-shows" },
  { icon: User, label: "Struggling pupils you can't help" },
  { icon: Megaphone, label: "Hard to stand out from other instructors" },
  { icon: Frown, label: "Feeling alone and unsupported" },
];

const FEATURES = [
  { icon: CalendarDays, title: "Run Your Business in One Place", body: "Bookings, reminder templates, payments and diary — all in one workspace." },
  { icon: Megaphone, title: "Get Found by Learners", body: "Verified marketplace listing + Smart Match — learners find you, not the other way round.", isBeta: true },
  { icon: Brain, title: "AI Lesson Feedback", body: "Upload clips, get AI feedback and improve faster.", isNew: true },
  { icon: IdCard, title: "Learner Passport & Transfer Network", body: "Can't help a pupil further? Transfer them without losing progress." },
  { icon: Gift, title: "Reward System", body: "Earn credits and unlock benefits for your growth and success." },
  { icon: GraduationCap, title: "PDI Development Hub", body: "Guidance, mock tests, tools and support to help you pass and thrive." },
];

function PainAndPlatform() {
  return (
    <section className="relative pb-16 md:pb-24" id="solutions">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-5 md:gap-6">
        {/* LEFT — Sound familiar? */}
        <aside
          data-testid="sound-familiar"
          className="lg:col-span-4 rounded-2xl bg-[#0a0a0a] border border-white/[0.07] p-6 md:p-7"
        >
          <h2 className="font-display text-2xl md:text-[28px] font-semibold tracking-tight mb-5">
            Sound familiar?
          </h2>
          <ul className="space-y-2.5">
            {PAINS.map(({ icon: Icon, label }, i) => (
              <li
                key={i}
                data-testid={`pain-${i}`}
                className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-[#0d0d0d] hover:bg-[#111] hover:border-white/15 transition px-3.5 py-2.5"
              >
                <span className="w-7 h-7 rounded-full bg-white/[0.04] border border-white/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-3.5 h-3.5 text-zinc-400" />
                </span>
                <span className="text-[13px] md:text-sm text-zinc-200 leading-tight">
                  {label}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-7 text-base md:text-lg text-white">
            You're not alone.{" "}
            <span className="relative inline-block">
              <span className="font-semibold">We've got you.</span>
              <span className="absolute left-0 right-0 -bottom-0.5 h-[3px] bg-[#D4AF37] rounded-full" />
            </span>
          </p>
        </aside>

        {/* RIGHT — All-in-one platform */}
        <div className="lg:col-span-8">
          <div className="rounded-2xl bg-[#0a0a0a] border border-white/[0.07] p-6 md:p-7 lg:p-8">
            <h2
              data-testid="platform-title"
              className="font-display text-2xl md:text-3xl lg:text-[34px] font-semibold tracking-tight leading-tight mb-1"
            >
              The all-in-one platform that{" "}
              <span className="text-gold-gradient">changes everything.</span>
            </h2>
            <p className="text-[13px] md:text-sm text-zinc-400 mb-6 md:mb-8 max-w-2xl">
              Every tool below is wired to give you back hours and turn good teaching into a
              real, growing business.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {FEATURES.map((f, i) => (
                <FeatureCard key={i} index={i} {...f} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ icon: Icon, title, body, isNew, isBeta, index }) {
  return (
    <div
      data-testid={`feature-${index}`}
      className="relative rounded-xl bg-[#0d0d0d] border border-white/[0.07] p-4 md:p-5 hover:border-[#D4AF37]/35 hover:-translate-y-0.5 transition group"
    >
      {isNew && (
        <span
          data-testid={`feature-${index}-new`}
          className="absolute -top-2 right-3 text-[9px] font-extrabold tracking-[0.15em] uppercase bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] text-[#050505] px-2 py-0.5 rounded-sm shadow"
        >
          NEW
        </span>
      )}
      {isBeta && !isNew && (
        <span
          data-testid={`feature-${index}-beta`}
          className="absolute -top-2 right-3 text-[9px] font-extrabold tracking-[0.15em] uppercase bg-indigo-500/90 text-white px-2 py-0.5 rounded-sm shadow ring-1 ring-indigo-300/40"
        >
          EARLY ACCESS
        </span>
      )}
      <div className="w-10 h-10 md:w-11 md:h-11 rounded-lg flex items-center justify-center mb-3 bg-gradient-to-br from-[#D4AF37]/20 to-transparent border border-[#D4AF37]/25">
        <Icon className="w-[18px] h-[18px] md:w-5 md:h-5 text-[#F3E5AB]" />
      </div>
      <h3 className="font-display text-[15px] md:text-base font-semibold leading-tight mb-2">
        {title}
      </h3>
      <p className="text-[11.5px] md:text-[12.5px] text-zinc-400 leading-snug">
        {body}
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* BOTTOM ROW — testimonial / community / final CTA                    */
/* ─────────────────────────────────────────────────────────────────── */

function BottomRow({ primaryHref }) {
  const [sectionRef, markCtaClicked] = useSectionEngagement("final_cta");
  return (
    <section ref={sectionRef} id="audiences" className="relative pb-12 md:pb-20">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 grid lg:grid-cols-3 gap-5 md:gap-6">
        <TestimonialCard />
        <CommunityCard />
        <FinalCTACard primaryHref={primaryHref} markCtaClicked={markCtaClicked} />
      </div>
    </section>
  );
}

function TestimonialCard() {
  return (
    <article
      data-testid="testimonial-sarah"
      className="rounded-2xl bg-[#0a0a0a] border border-white/[0.07] p-6 md:p-7 flex flex-col"
    >
      <div className="flex items-center gap-1 text-[#D4AF37] mb-4">
        {[0, 1, 2, 3, 4].map((i) => (
          <Star key={i} className="w-4 h-4 fill-current" />
        ))}
      </div>
      <Quote className="w-5 h-5 text-[#D4AF37]/50 mb-3" aria-hidden />
      <blockquote className="text-[15px] md:text-base text-white/95 leading-relaxed flex-1">
        "The Guild has completely transformed the way I run my business. I've got more time, more
        learners and more confidence. It's a no-brainer."
      </blockquote>
      <figcaption className="mt-6 flex items-center gap-3">
        <img
          src={TESTIMONIAL_AVATARS[0]}
          alt="Sarah T."
          className="w-11 h-11 rounded-full object-cover ring-1 ring-white/10"
          loading="lazy"
        />
        <div>
          <div className="text-sm font-semibold">— Sarah T.</div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">ADI &amp; Guild Member</div>
        </div>
      </figcaption>
    </article>
  );
}

function CommunityCard() {
  return (
    <article
      data-testid="community-card"
      className="rounded-2xl bg-[#0a0a0a] border border-white/[0.07] p-6 md:p-7 text-center flex flex-col items-center justify-center"
    >
      <h3 className="font-display text-2xl md:text-[28px] font-semibold tracking-tight leading-tight">
        It's more than software.
      </h3>
      <h3 className="font-display text-2xl md:text-[28px] font-semibold tracking-tight leading-tight">
        It's <span className="text-gold-gradient">your support system.</span>
      </h3>
      <p className="mt-4 text-sm text-zinc-400 leading-relaxed max-w-md">
        Join a community of instructors who are raising standards, helping each other and
        building better businesses.
      </p>
      <div className="mt-6 flex -space-x-2.5">
        {TESTIMONIAL_AVATARS.concat(TESTIMONIAL_AVATARS.slice(0, 2)).map((src, i) => (
          <img
            key={i}
            src={src}
            alt=""
            className="w-10 h-10 rounded-full object-cover ring-2 ring-[#0a0a0a] border border-white/10"
            loading="lazy"
          />
        ))}
        <span className="w-10 h-10 rounded-full bg-[#1a1a1a] ring-2 ring-[#0a0a0a] border border-white/10 flex items-center justify-center text-[10px] font-bold text-[#F3E5AB]">
          +1k
        </span>
      </div>
    </article>
  );
}

function FinalCTACard({ primaryHref, markCtaClicked }) {
  return (
    <article
      data-testid="final-cta-card"
      className="rounded-2xl bg-gradient-to-b from-[#16120a] to-[#0a0a0a] border border-[#D4AF37]/35 p-6 md:p-7 text-center flex flex-col items-center justify-center guild-card-glow"
    >
      <h3 className="font-display text-xl md:text-2xl lg:text-[26px] font-semibold tracking-tight leading-tight text-gold-gradient">
        Ready to take back control
      </h3>
      <h3 className="font-display text-xl md:text-2xl lg:text-[26px] font-semibold tracking-tight leading-tight text-white mt-1">
        and build the business
      </h3>
      <h3 className="font-display text-xl md:text-2xl lg:text-[26px] font-semibold tracking-tight leading-tight text-white">
        you deserve?
      </h3>
      <Link to={primaryHref} onClick={() => { markCtaClicked?.({ cta: "join_today" }); trackCta("landing_final_cta_clicked", "Join The Guild Today", { destination: primaryHref }); }} className="mt-7 w-full">
        <Button
          data-testid="final-cta-btn"
          className="w-full h-12 rounded-md bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] hover:from-[#FAF0C0] hover:to-[#E5C04A] text-[#050505] font-bold text-[15px] guild-cta-glow"
        >
          Join The Guild Today <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </Link>
      <p className="mt-3 text-xs text-zinc-500">No contracts. Cancel anytime.</p>
    </article>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* EASY SWITCH — kills switching anxiety                              */
/* ─────────────────────────────────────────────────────────────────── */

const SWITCH_BULLETS = [
  { icon: Zap, label: "Set up in minutes" },
  { icon: Compass, label: "Guided onboarding" },
  { icon: ArrowRightLeft, label: "Transfer key learner details" },
  { icon: CalendarDays, label: "Rebuild diary quickly" },
  { icon: Sparkles, label: "Keep teaching during the switch" },
  { icon: HeartHandshake, label: "Friendly support available" },
];

const SWITCH_TIMELINE = [
  {
    badge: "Day 0",
    title: "30-minute setup",
    body: "Account live, branding ready, calendar imported. We do the heavy lifting on the call.",
  },
  {
    badge: "Day 1",
    title: "Learners migrated",
    body: "Active pupils transferred with their progress, contact details and lesson history intact.",
  },
  {
    badge: "Day 7",
    title: "Fully running",
    body: "Diary running, reminders ready to send, your old admin headaches behind you.",
  },
];

const SWITCH_ORIGINS = [
  "Total Drive",
  "BookFast",
  "myDriveSchool",
  "Spreadsheet",
  "Other",
];

function EasySwitch({ primaryHref }) {
  const [origin, setOrigin] = useState(null);
  const [sectionRef, markCtaClicked] = useSectionEngagement("easy_switch");

  const selectOrigin = (label) => {
    setOrigin(label);
    trackCta("landing_switch_origin_clicked", label, { source_system: label });
  };
  return (
    <section
      ref={sectionRef}
      id="easy-switch"
      data-testid="easy-switch-section"
      className="relative pb-20 md:pb-28"
    >
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        <div className="rounded-3xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#0e0c08] via-[#0a0a0a] to-[#0a0a0a] guild-card-glow overflow-hidden">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 p-7 md:p-10 lg:p-12">
            {/* LEFT — copy */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] px-3 py-1 text-[10.5px] uppercase tracking-[0.22em] text-[#F3E5AB] mb-5">
                <ArrowRightLeft className="w-3 h-3" />
                Easy switch
              </div>

              <h2
                data-testid="easy-switch-title"
                className="font-display font-semibold text-3xl md:text-4xl lg:text-[44px] tracking-tight leading-[1.1] text-white"
              >
                Love what you see?{" "}
                <span className="text-gold-gradient">Switching is easier than you think.</span>
              </h2>

              <p
                data-testid="easy-switch-sub"
                className="mt-5 text-base md:text-lg text-zinc-300 leading-relaxed max-w-xl"
              >
                We help instructors move from their current setup quickly and smoothly, without
                disrupting your working week.
              </p>

              <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 max-w-xl">
                {SWITCH_BULLETS.map(({ icon: Icon, label }, i) => (
                  <li
                    key={i}
                    data-testid={`easy-switch-bullet-${i}`}
                    className="flex items-start gap-3 text-[14px] text-white/95"
                  >
                    <span className="mt-[2px] w-7 h-7 rounded-full bg-[#D4AF37]/[0.10] border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-3.5 h-3.5 text-[#F3E5AB]" />
                    </span>
                    <span className="leading-tight">{label}</span>
                  </li>
                ))}
              </ul>

              <div
                data-testid="easy-switch-trust"
                className="mt-8 inline-flex items-center gap-2.5 rounded-xl border border-white/[0.08] bg-white/[0.02] px-4 py-3 text-[13px] md:text-sm text-white/90"
              >
                <ShieldCheck className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                <span>
                  <span className="font-semibold">No chaos.</span> No starting from scratch.{" "}
                  <span className="hidden sm:inline">No long migration headache.</span>
                </span>
              </div>

              {/* Origin chip row — signals "we know your current tool" */}
              <div className="mt-8" data-testid="switch-origin-row">
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-400 font-semibold mb-3">
                  Coming from?
                </div>
                <div className="flex flex-wrap gap-2">
                  {SWITCH_ORIGINS.map((label) => {
                    const on = origin === label;
                    return (
                      <button
                        key={label}
                        data-testid={`switch-origin-${label.toLowerCase().replace(/\s+/g, "-")}`}
                        data-state={on ? "on" : "off"}
                        onClick={() => selectOrigin(label)}
                        className={`text-[12.5px] font-semibold rounded-full px-3.5 py-1.5 border transition active:scale-[0.97] ${
                          on
                            ? "bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] text-[#050505] border-[#D4AF37] shadow-[0_0_18px_-4px_rgba(212,175,55,0.55)]"
                            : "bg-white/[0.03] text-zinc-200 border-white/[0.10] hover:bg-white/[0.06] hover:border-[#D4AF37]/35"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <p
                  data-testid="switch-origin-helper"
                  className={`text-[12px] text-[#F3E5AB] mt-3 inline-flex items-center gap-1.5 transition-opacity duration-300 ${origin ? "opacity-100" : "opacity-0 h-0 overflow-hidden"}`}
                  aria-live="polite"
                >
                  <Sparkles className="w-3 h-3" />
                  We'll help guide your switch from {origin}.
                </p>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link
                  to={primaryHref}
                  onClick={() => {
                    markCtaClicked({ cta: "start_switch", source_system: origin || null });
                    trackCta("landing_switch_cta_clicked", "Start Your Easy Switch", {
                      destination: primaryHref,
                      source_system: origin || null,
                    });
                  }}
                >
                  <Button
                    data-testid="easy-switch-cta"
                    className="h-12 px-6 rounded-md bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] hover:from-[#FAF0C0] hover:to-[#E5C04A] text-[#050505] text-[14px] font-bold guild-cta-glow w-full sm:w-auto"
                  >
                    Start Your Easy Switch <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <a
                  href="mailto:hello@nationaldrivingguild.co.uk?subject=Easy%20Switch%20Chat"
                  onClick={() => {
                    markCtaClicked({ cta: "book_chat" });
                    trackCta("landing_switch_chat_clicked", "Book a Quick Chat", {
                      destination: "mailto",
                    });
                  }}
                >
                  <Button
                    data-testid="easy-switch-chat"
                    variant="outline"
                    className="h-12 px-6 rounded-md bg-transparent border border-white/15 hover:bg-white/5 text-white text-[14px] font-semibold w-full sm:w-auto"
                  >
                    <MessagesSquare className="w-4 h-4 mr-2 text-[#D4AF37]" />
                    Book a Quick Chat
                  </Button>
                </a>
              </div>
            </div>

            {/* RIGHT — timeline visual */}
            <div className="lg:col-span-5">
              <div
                data-testid="easy-switch-timeline"
                className="relative rounded-2xl bg-[#0d0d0d] border border-white/[0.07] p-5 md:p-6 h-full"
              >
                {/* Vertical gold thread */}
                <div className="absolute left-[34px] md:left-[38px] top-[38px] bottom-[26px] w-px bg-gradient-to-b from-[#D4AF37]/70 via-[#D4AF37]/40 to-transparent pointer-events-none" aria-hidden />

                <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-400 mb-5 flex items-center gap-2">
                  <span className="w-6 h-px bg-[#D4AF37]/60" />
                  Your switch · 7 days
                </div>

                <ol className="space-y-5">
                  {SWITCH_TIMELINE.map((step, i) => (
                    <li
                      key={i}
                      data-testid={`easy-switch-step-${i}`}
                      className="relative flex gap-4"
                    >
                      <div className="relative z-10 w-7 h-7 md:w-8 md:h-8 rounded-full bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] flex items-center justify-center text-[#050505] text-[10px] md:text-[11px] font-extrabold flex-shrink-0 shadow-[0_0_0_4px_#0d0d0d]">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                            {step.badge}
                          </span>
                          <span className="text-zinc-700">·</span>
                          <span className="text-[13px] md:text-sm font-semibold text-white">
                            {step.title}
                          </span>
                        </div>
                        <p className="text-[12.5px] md:text-[13px] text-zinc-400 leading-relaxed">
                          {step.body}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>

                <div className="mt-6 pt-5 border-t border-white/[0.06] flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/[0.10] border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                    <HeartHandshake className="w-4 h-4 text-[#F3E5AB]" />
                  </div>
                  <p className="text-[12.5px] text-zinc-300 leading-snug">
                    A real human walks you through it. <span className="text-zinc-500">No automated runaround.</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* GUILD MEMBERSHIP — prestige, specialist profile, visibility         */
/* ─────────────────────────────────────────────────────────────────── */

const MEMBERSHIP_BENEFITS = [
  {
    icon: Award,
    title: "NDG Verified Badge",
    body: "A premium, visible trust badge awarded to instructors who meet Guild standards and uphold them through their work.",
  },
  {
    icon: Star,
    title: "Specialist Showcase Profile",
    body: "Highlight your strengths — nervous learners, automatic, manual, neurodivergent support, intensives, calm coaching, refresher lessons, and more.",
  },
  {
    icon: IdCard,
    title: "Instructor Passport Profile",
    body: "A premium public profile showing your best attributes, teaching style, availability, specialties, trust signals and verified status.",
  },
  {
    icon: Eye,
    title: "Better Match Visibility",
    body: "Verified specialists earn stronger trust signals when learners use Search or Best Match — discovered by the pupils who need them most.",
  },
];

const VERIFIED_CRITERIA = [
  { icon: User, label: "Professional profile completed" },
  { icon: Sparkles, label: "Active platform engagement" },
  { icon: MessagesSquare, label: "Good communication & response standards" },
  { icon: Heart, label: "Positive learner feedback" },
  { icon: ShieldCheck, label: "Compliance kept up to date" },
  { icon: Trophy, label: "Commitment to high standards" },
];

const SHOWCASE_SPECIALTIES = [
  "Nervous learners",
  "Automatic",
  "Manual mastery",
  "ADHD / autism support",
  "Intensive courses",
  "Calm coaching",
  "Female instructor",
  "Refresher lessons",
  "Confidence building",
];

/** Premium gold guild badge — cinematic, used as a focal trust mark. */
function GuildBadge({ size = 110 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 110 110"
      aria-hidden
      className="flex-shrink-0 drop-shadow-[0_8px_30px_rgba(212,175,55,0.35)]"
    >
      <defs>
        <linearGradient id="badge-gold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FFF6D2" />
          <stop offset="0.45" stopColor="#F3E5AB" />
          <stop offset="0.85" stopColor="#D4AF37" />
          <stop offset="1" stopColor="#7E5A14" />
        </linearGradient>
        <radialGradient id="badge-glow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="rgba(212,175,55,0.45)" />
          <stop offset="1" stopColor="rgba(212,175,55,0)" />
        </radialGradient>
      </defs>
      <circle cx="55" cy="55" r="55" fill="url(#badge-glow)" />
      {/* Outer rim */}
      <circle cx="55" cy="55" r="44" fill="none" stroke="url(#badge-gold)" strokeWidth="1.5" />
      {/* Tooth-edge medal */}
      <g fill="url(#badge-gold)">
        {Array.from({ length: 24 }).map((_, i) => {
          const a = (i / 24) * Math.PI * 2;
          const x1 = 55 + Math.cos(a) * 41;
          const y1 = 55 + Math.sin(a) * 41;
          const x2 = 55 + Math.cos(a) * 47;
          const y2 = 55 + Math.sin(a) * 47;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="url(#badge-gold)" strokeWidth="1.2" />;
        })}
      </g>
      {/* Inner medallion */}
      <circle cx="55" cy="55" r="36" fill="#0a0a0a" stroke="url(#badge-gold)" strokeWidth="1" />
      {/* Star */}
      <polygon
        points="55,33 58.6,45.2 71.3,45.2 61,52.7 64.7,64.9 55,57.3 45.3,64.9 49,52.7 38.7,45.2 51.4,45.2"
        fill="url(#badge-gold)"
      />
      {/* Banner / wordmark */}
      <text
        x="55" y="78" textAnchor="middle"
        fontFamily="Outfit, sans-serif" fontWeight="800" fontSize="6.5"
        fill="url(#badge-gold)" letterSpacing="2.4"
      >NDG VERIFIED</text>
      <text
        x="55" y="86" textAnchor="middle"
        fontFamily="Outfit, sans-serif" fontWeight="700" fontSize="4.5"
        fill="rgba(212,175,55,0.65)" letterSpacing="1.8"
      >EARNED EXCELLENCE</text>
    </svg>
  );
}

function GuildMembership({ primaryHref }) {
  const [sectionRef, markCtaClicked] = useSectionEngagement("membership");
  return (
    <section
      ref={sectionRef}
      id="guild-membership"
      data-testid="guild-membership-section"
      className="relative pb-20 md:pb-28"
    >
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* LEFT — copy + benefits */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] px-3 py-1 text-[10.5px] uppercase tracking-[0.22em] text-[#F3E5AB] mb-5">
              <Award className="w-3 h-3" />
              Guild Membership · Verified Excellence
            </div>

            <h2
              data-testid="guild-membership-title"
              className="font-display font-semibold text-3xl md:text-4xl lg:text-[44px] tracking-tight leading-[1.08] text-white"
            >
              Join The Guild.{" "}
              <span className="text-gold-gradient">Become Verified.</span> Showcase what makes you exceptional.
            </h2>

            <p
              data-testid="guild-membership-sub"
              className="mt-5 text-base md:text-lg text-zinc-300 leading-relaxed max-w-2xl"
            >
              Members can earn <span className="text-white font-semibold">National Driving Guild Verified</span> status
              through professionalism, standards and active engagement — helping build trust and attract the right
              learners. Awarded when Guild criteria are met.
            </p>

            <div className="mt-9 grid grid-cols-1 md:grid-cols-2 gap-4">
              {MEMBERSHIP_BENEFITS.map(({ icon: Icon, title, body }, i) => (
                <article
                  key={i}
                  data-testid={`guild-benefit-${i}`}
                  className="rounded-2xl bg-[#0a0a0a] border border-white/[0.07] p-5 hover:border-[#D4AF37]/35 transition group"
                >
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#D4AF37]/20 to-transparent border border-[#D4AF37]/25 flex items-center justify-center mb-3">
                    <Icon className="w-[18px] h-[18px] text-[#F3E5AB]" />
                  </div>
                  <h3 className="font-display text-[16px] md:text-[17px] font-semibold leading-tight mb-1.5">
                    {title}
                  </h3>
                  <p className="text-[12.5px] md:text-[13px] text-zinc-400 leading-snug">
                    {body}
                  </p>
                </article>
              ))}
            </div>

            {/* What Verified Means — standards-based recognition */}
            <div
              data-testid="verified-criteria-block"
              className="mt-6 rounded-2xl border border-white/[0.07] bg-white/[0.015] p-5 md:p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <BadgeCheck className="w-4 h-4 text-[#D4AF37]" />
                <h3 className="font-display text-[15px] md:text-base font-semibold tracking-tight">
                  What <span className="text-gold-gradient">NDG Verified</span> means
                </h3>
                <span className="ml-auto text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold hidden sm:inline">
                  Standards-based recognition
                </span>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {VERIFIED_CRITERIA.map(({ icon: Icon, label }, i) => (
                  <li
                    key={i}
                    data-testid={`verified-criterion-${i}`}
                    className="flex items-start gap-2.5 text-[13px] text-zinc-200"
                  >
                    <span className="mt-[2px] w-5 h-5 rounded-full bg-[#D4AF37]/[0.10] border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-3 h-3 text-[#F3E5AB]" />
                    </span>
                    <span className="leading-tight">{label}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-[11.5px] text-zinc-500 leading-snug">
                Verified status isn't bought — it's <span className="text-zinc-300">awarded when Guild criteria are
                met</span> and reviewed regularly to keep the standard meaningful.
              </p>
            </div>

            <div
              data-testid="guild-trust-line"
              className="mt-6 inline-flex items-start gap-2.5 rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/[0.05] px-4 py-3 text-[13px] md:text-sm text-white/95"
            >
              <ShieldCheck className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-[2px]" />
              <span>
                <span className="font-semibold">Earn recognition.</span>{" "}
                <span className="font-semibold">Build trust.</span>{" "}
                Be found for what you do best.
              </span>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to={primaryHref}
                onClick={() => {
                  markCtaClicked({ cta: "earn_verified" });
                  trackCta("landing_membership_cta_clicked", "Earn Your Verified Status", {
                    destination: primaryHref,
                  });
                }}
              >
                <Button
                  data-testid="guild-membership-cta"
                  className="h-12 px-6 rounded-md bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] hover:from-[#FAF0C0] hover:to-[#E5C04A] text-[#050505] text-[14px] font-bold guild-cta-glow w-full sm:w-auto"
                >
                  Earn Your Verified Status <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link
                to="/marketplace"
                onClick={() => {
                  markCtaClicked({ cta: "see_verified" });
                  trackCta("landing_membership_view_clicked", "See Verified Members", {
                    destination: "/marketplace",
                  });
                }}
              >
                <Button
                  data-testid="guild-membership-view"
                  variant="outline"
                  className="h-12 px-6 rounded-md bg-transparent border border-white/15 hover:bg-white/5 text-white text-[14px] font-semibold w-full sm:w-auto"
                >
                  See Verified Members
                </Button>
              </Link>
            </div>
          </div>

          {/* RIGHT — Instructor Passport profile mockup */}
          <div className="lg:col-span-5">
            <PassportProfileMock />
          </div>
        </div>
      </div>
    </section>
  );
}

/** Stylised instructor profile card — premium passport-style mockup. */
function PassportProfileMock() {
  return (
    <div
      data-testid="guild-passport-mock"
      className="relative rounded-3xl bg-gradient-to-br from-[#16120a] via-[#0c0c0c] to-[#0a0a0a] border border-[#D4AF37]/30 guild-card-glow p-6 md:p-7 overflow-hidden"
    >
      {/* Subtle gold light leak */}
      <div
        aria-hidden
        className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-20 pointer-events-none"
        style={{ background: "radial-gradient(circle at center, #D4AF37 0%, transparent 70%)" }}
      />
      {/* Header — passport label */}
      <div className="flex items-center justify-between mb-6 relative">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold flex items-center gap-2">
          <span className="w-6 h-px bg-[#D4AF37]/60" />
          Instructor Passport
        </div>
        <span className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-[0.2em] font-bold text-zinc-300 border border-white/10 bg-white/[0.03] rounded-full px-2 py-0.5">
          <Trophy className="w-2.5 h-2.5 text-[#D4AF37]" /> Master tier
        </span>
      </div>

      {/* Hero — avatar + name + badge */}
      <div className="flex items-start gap-4 relative">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#D4AF37]/30 to-[#0a0a0a] border border-[#D4AF37]/30 flex items-center justify-center text-[#F3E5AB] text-2xl font-display font-bold flex-shrink-0">
          JR
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl font-semibold leading-tight">James R.</div>
          <div className="text-[12px] text-zinc-400 mt-0.5 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> Manchester · M1</span>
            <span className="text-zinc-700">·</span>
            <span className="inline-flex items-center gap-1"><PoundSterling className="w-3 h-3" />45/hr</span>
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 rounded-full px-2 py-0.5">
              <ShieldCheck className="w-2.5 h-2.5" /> Compliant
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-[#D4AF37]/15 text-[#F3E5AB] border border-[#D4AF37]/40 rounded-full px-2 py-0.5">
              <BadgeCheck className="w-2.5 h-2.5" /> NDG Verified
            </span>
          </div>
        </div>
        <div className="hidden md:block flex-shrink-0">
          <GuildBadge size={88} />
        </div>
      </div>

      {/* Mobile-only badge — centred under header */}
      <div className="md:hidden flex justify-center mt-5 mb-1">
        <GuildBadge size={92} />
      </div>

      {/* Specialisms showcase */}
      <div className="mt-6 relative">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-2.5 flex items-center gap-2">
          <Sparkles className="w-3 h-3 text-[#D4AF37]" />
          Specialisms
        </div>
        <div className="flex flex-wrap gap-1.5">
          {SHOWCASE_SPECIALTIES.map((s, i) => (
            <span
              key={i}
              data-testid={`guild-specialty-${i}`}
              className={`text-[11px] font-semibold rounded-full px-2.5 py-1 ${
                i < 4
                  ? "bg-[#D4AF37]/15 text-[#F3E5AB] border border-[#D4AF37]/35"
                  : "bg-white/[0.04] text-zinc-300 border border-white/10"
              }`}
            >
              {s}
            </span>
          ))}
        </div>
      </div>

      {/* Stats / proof */}
      <div className="mt-6 grid grid-cols-3 gap-2 relative">
        {[
          { icon: Star, value: "4.9", label: "Avg rating", iconFill: true },
          { icon: Users, value: "180+", label: "Pupils helped" },
          { icon: Heart, value: "94%", label: "Pass rate" },
        ].map(({ icon: Icon, value, label, iconFill }, i) => (
          <div key={i} className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3 text-center">
            <Icon className={`w-3.5 h-3.5 text-[#D4AF37] mx-auto mb-1 ${iconFill ? "fill-[#D4AF37]" : ""}`} />
            <div className="text-base font-display font-bold leading-none text-white">{value}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">{label}</div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="mt-5 pt-4 border-t border-white/[0.06] flex items-center justify-between">
        <span className="text-[11px] text-zinc-500 inline-flex items-center gap-1.5">
          <Calendar className="w-3 h-3 text-[#D4AF37]" /> Mon–Fri · 09:00–18:00
        </span>
        <span className="inline-flex items-center gap-1 text-[10.5px] font-bold text-emerald-300">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 guild-pulse" />
          Accepting pupils
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* TRUST FOOTER ROW                                                    */
/* ─────────────────────────────────────────────────────────────────── */

const TRUST_ITEMS = [
  { icon: MapPin, label: "UK-Based Support When You Need It" },
  { icon: Lock, label: "Secure & Private" },
  { icon: HeartHandshake, label: "Real Help. No Runaround." },
  { icon: Rocket, label: "Built By People Who Get Instructors" },
];

function TrustFooter() {
  return (
    <section className="relative pb-16 md:pb-24">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        <div
          data-testid="trust-footer"
          className="border-t border-white/[0.06] pt-8 grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-4 md:divide-x md:divide-white/[0.06]"
        >
          {TRUST_ITEMS.map(({ icon: Icon, label }, i) => (
            <div
              key={i}
              data-testid={`trust-foot-${i}`}
              className="flex items-center justify-center gap-2.5 text-zinc-300 text-[12.5px] md:text-sm font-medium"
            >
              <Icon className="w-4 h-4 text-[#D4AF37]" />
              <span>{label}</span>
            </div>
          ))}
        </div>

        <div className="mt-10 pt-6 border-t border-white/[0.04] flex flex-col md:flex-row gap-4 md:items-center md:justify-between text-[11px] text-zinc-500">
          <div className="flex items-center gap-2.5">
            <NdgShield size={22} />
            <span className="font-condensed text-zinc-300 tracking-wider">NATIONAL DRIVING GUILD</span>
            <span className="hidden md:inline text-zinc-600">·</span>
            <span className="hidden md:inline">A professional home for UK driving instructors.</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/terms" data-testid="footer-terms" className="hover:text-zinc-200 transition">Terms</Link>
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-zinc-200 transition">Privacy</Link>
            <Link to="/pricing" data-testid="footer-pricing" className="hover:text-zinc-200 transition">Pricing</Link>
          </div>
        </div>
      </div>
    </section>
  );
}
